import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import CompanyLogoUpload from "../../CompanySettings/Appearance/CompanyLogoUpload";
import CompanyMailLogoUpload from "../../CompanySettings/Appearance/CompanyMailLogoUpload";
import LogoImage from "./LogoImage";

const ui = {
  pageBg: "#f7f8fa",
  panel: "#ffffff",
  railBg: "#ffffff",
  border: "#e6e9ee",
  borderStrong: "#d9dee7",
  text: "#182230",
  muted: "#667085",
  subtle: "#98a2b3",
  soft: "#f8fafc",
  successBg: "#eefaf2",
  successBorder: "#b7e4c4",
  successText: "#17733b",
};

const LogoDialog = ({
  open,
  onClose,
  disabled,
  companyId,
  platformLogoPreview,
  emailLogoPreview,
  onPlatformUploaded,
  onEmailUploaded,
}) => {
  const intl = useIntl();

  const renderLogoRow = ({
    titleId,
    formatId,
    preview,
    uploadControl,
    warningId,
  }) => (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${ui.border}`,
        borderRadius: 0.75,
        p: 1.35,
        bgcolor: "#fff",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 1, sm: 1.35 }}
        alignItems={{ xs: "stretch", sm: "center" }}
      >
        <Box
          sx={{
            width: { xs: "100%", sm: 94 },
            height: { xs: 72, sm: 60 },
            border: `1px solid ${ui.borderStrong}`,
            borderRadius: 0.75,
            bgcolor: ui.soft,
            display: "grid",
            placeItems: "center",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <LogoImage
            src={preview}
            alt={intl.formatMessage({ id: titleId })}
            maxWidth={80}
            maxHeight={46}
            placeholderSize={20}
          />
        </Box>

        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography sx={{ fontSize: 12.8, fontWeight: 700, color: ui.text }}>
            <FormattedMessage id={titleId} />
          </Typography>
          <Typography sx={{ mt: 0.25, color: ui.subtle, fontSize: 10.7 }}>
            <FormattedMessage id={formatId} />
          </Typography>
          <Typography sx={{ mt: 0.25, color: ui.muted, fontSize: 10.55, lineHeight: 1.35 }}>
            <FormattedMessage id={warningId} />
          </Typography>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            width: { xs: "100%", sm: "auto" },
            pointerEvents: disabled || !companyId ? "none" : "auto",
            opacity: disabled || !companyId ? 0.45 : 1,
            "& > div": {
              width: { xs: "100%", sm: "fit-content" },
            },
            "& button": {
              width: { xs: "100%", sm: "auto" },
              minHeight: { xs: "40px !important", sm: "32px !important" },
              borderRadius: "6px !important",
              fontSize: "11.5px !important",
            },
          }}
        >
          {uploadControl}
        </Box>
      </Stack>
    </Paper>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          m: { xs: 1, sm: 2 },
          width: { xs: "calc(100% - 16px)", sm: "100%" },
          maxHeight: { xs: "calc(100% - 16px)", sm: "calc(100% - 64px)" },
        },
      }}
    >
      <DialogTitle sx={{ px: { xs: 1.5, sm: 3 }, pb: 0.75, fontSize: 16, fontWeight: 700 }}>
        <FormattedMessage id="admin.organizationDetails.appearanceAndBranding" />
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 1.5, sm: 3 }, pt: 1 }}>
        <Stack spacing={1.15}>
          {renderLogoRow({
            titleId: "admin.companySettings.companyLogoPlatform.Title",
            formatId: "admin.organizationDetails.logoPlatformFormats",
            preview: platformLogoPreview,
            warningId: "admin.companySettings.companyLogoPlatform.Warning",
            uploadControl: (
              <CompanyLogoUpload
                companyId={companyId || 0}
                onUploaded={onPlatformUploaded}
              />
            ),
          })}

          {renderLogoRow({
            titleId: "admin.companySettings.companyLogoMail.Title",
            formatId: "admin.organizationDetails.logoEmailFormats",
            preview: emailLogoPreview,
            warningId: "admin.companySettings.companyLogoMail.Warning",
            uploadControl: (
              <CompanyMailLogoUpload
                companyId={companyId || 0}
                onUploaded={onEmailUploaded}
              />
            ),
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 1.5, sm: 3 }, pb: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          <FormattedMessage id="btn.close" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

LogoDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  companyId: PropTypes.number,
  platformLogoPreview: PropTypes.string,
  emailLogoPreview: PropTypes.string,
  onPlatformUploaded: PropTypes.func.isRequired,
  onEmailUploaded: PropTypes.func.isRequired,
};

LogoDialog.defaultProps = {
  companyId: null,
  platformLogoPreview: "",
  emailLogoPreview: "",
};

LogoDialog.displayName = "LogoDialog";

export default LogoDialog;
